// Unit-aware quote prices. A negotiated price is a number agreed against ONE
// rung of the master product's packaging ladder — "198 per carton" is not the
// same claim as "198 per kg", and for a 1 carton × 6 pieces × 2 kg product the
// two differ by 12×. The match matrix used to render `price + defaultUnit`,
// which concatenates a number and an unrelated unit string; this module does
// the arithmetic instead.
//
// The ladder is the master-products model (see describe-units.ts for the same
// field semantics): PACK (packUnit, e.g. carton) contains `packSize` ITEMs
// (itemUnit, e.g. piece), and each ITEM contains `netContent` BASEs (baseUnit,
// e.g. kg).
//
//   PACK ──packSize──▶ ITEM ──netContent──▶ BASE
//
// Tiers are stored as ladder POSITIONS, never as unit words: `PACK` resolves
// through the master's current packUnit, so correcting a master's packaging
// re-renders old quotes correctly instead of stranding a literal "carton".
//
// Only `baseUnit` is required on a master product. Absent netContent/packSize
// mean UNKNOWN, never 1 — a rung we cannot compute is omitted rather than
// guessed.

import { round } from 'pages/inquiry-processor/utils/describe-units';

export type PriceTier = 'PACK' | 'ITEM' | 'BASE';

// Structural, so a MasterProductSummary, a serialized `product.masterProduct`,
// or a hand-built ladder can all be passed straight in.
export interface UomLadder {
  baseUnit?: string | null;
  itemUnit?: string | null;
  netContent?: number | null;
  packUnit?: string | null;
  packSize?: number | null;
}

// Outermost first — the order the UI lists tiers in, and the precedence
// resolveTier falls back through.
export const TIER_ORDER: PriceTier[] = ['PACK', 'ITEM', 'BASE'];

const positive = (n?: number | null): number | null => (n != null && Number.isFinite(n) && n > 0 ? n : null);

// A supplier product's ladder lives on its linked master, not on the product
// row — unwrap it here so call sites stay one-liners, and an unlinked product
// yields an empty ladder rather than a crash.
export const ladderOf = (product?: { masterProduct?: UomLadder | null } | null): UomLadder =>
  product?.masterProduct ?? {};

// The unit word a tier is priced in, or null when the master doesn't define
// that rung.
export function tierUnit(ladder: UomLadder, tier: PriceTier): string | null {
  const raw = tier === 'PACK' ? ladder.packUnit : tier === 'ITEM' ? ladder.itemUnit : ladder.baseUnit;
  return raw?.trim() || null;
}

// Tiers a price can be quoted against, outermost first. Availability is about
// the unit WORD only: "198 per carton" is a complete, honest statement even
// when packSize is unknown and nothing can be derived from it.
export function availableTiers(ladder: UomLadder): { tier: PriceTier; unit: string }[] {
  return TIER_ORDER.map((tier) => ({ tier, unit: tierUnit(ladder, tier) })).filter(
    (entry): entry is { tier: PriceTier; unit: string } => entry.unit !== null
  );
}

// How much of the next rung down one of this rung holds. Index i is the hop
// from TIER_ORDER[i] to TIER_ORDER[i + 1]: PACK→ITEM is packSize items,
// ITEM→BASE is netContent base units. A null hop is an unknown, and blocks
// every conversion that would have to cross it.
const hopFactors = (ladder: UomLadder): (number | null)[] => [positive(ladder.packSize), positive(ladder.netContent)];

// Convert an agreed price between two rungs, walking the ladder one hop at a
// time. Going DOWN (carton → piece) divides by the hop factor: a carton of 6
// costs 6× what one piece costs. Going UP multiplies.
//
// Walking hop-by-hop rather than normalising through baseUnit matters when the
// ladder is partial: with packSize known but netContent null, PACK→ITEM is
// still exact (198 / 6 = 33) even though nothing can reach BASE.
//
// Returns null when any hop on the path is unknown.
export function convertPrice(price: number, from: PriceTier, to: PriceTier, ladder: UomLadder): number | null {
  if (!Number.isFinite(price)) return null;
  const fromIndex = TIER_ORDER.indexOf(from);
  const toIndex = TIER_ORDER.indexOf(to);
  if (fromIndex < 0 || toIndex < 0) return null;
  if (fromIndex === toIndex) return price;

  const factors = hopFactors(ladder);
  const step = fromIndex < toIndex ? 1 : -1;
  let amount = price;
  for (let i = fromIndex; i !== toIndex; i += step) {
    // Descending uses the hop leaving i; ascending uses the hop arriving at i.
    const factor = factors[step === 1 ? i : i - 1];
    if (factor == null) return null;
    amount = step === 1 ? amount / factor : amount * factor;
  }
  return amount;
}

// Every rung the price can be restated on, EXCLUDING the one it was agreed on
// (that stays the headline). Ordered outermost first, so a carton price reads
// "per piece, then per kg".
export function derivePrices(
  price: number,
  tier: PriceTier,
  ladder: UomLadder
): { tier: PriceTier; unit: string; amount: number }[] {
  return availableTiers(ladder)
    .filter((entry) => entry.tier !== tier)
    .map((entry) => ({ ...entry, amount: convertPrice(price, tier, entry.tier, ladder) }))
    .filter((entry): entry is { tier: PriceTier; unit: string; amount: number } => entry.amount != null);
}

// Which rung this quote's price sits on:
//   1. the operator's stored pick, when the master still defines that rung;
//   2. else the supplier SKU's free-text defaultUnit, if it names a rung —
//      a display-only inference for quotes predating the stored field;
//   3. else the outermost rung the master defines (the convention the GPO
//      price matrix already uses: packUnit || itemUnit || baseUnit).
// Null when the master defines no rung at all, which collapses callers back to
// the legacy `price + defaultUnit` rendering.
export function resolveTier(
  ladder: UomLadder,
  stored?: PriceTier | null,
  defaultUnit?: string | null
): PriceTier | null {
  const tiers = availableTiers(ladder);
  if (!tiers.length) return null;
  if (stored && tiers.some((entry) => entry.tier === stored)) return stored;

  const needle = defaultUnit?.trim().toLowerCase();
  const named = needle ? tiers.find((entry) => entry.unit.toLowerCase() === needle) : undefined;
  return named?.tier ?? tiers[0].tier;
}

// Whole numbers stay whole (33), fractions get exactly two decimals (16.50) —
// so a derived line reads as money without trailing-zero noise on round values.
export function formatMoney(amount: number): string {
  const value = round(amount);
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

// The muted "33 / piece · 16.50 / kg" line under a quote price. Null when
// nothing is derivable, so callers can drop the line entirely rather than
// render an empty row.
export function formatDerivedPrices(price: number, tier: PriceTier, ladder: UomLadder): string | null {
  const derived = derivePrices(price, tier, ladder);
  if (!derived.length) return null;
  return derived.map((entry) => `${formatMoney(entry.amount)} / ${entry.unit}`).join(' · ');
}
